import React, { useEffect, useState } from "react";
import { Check, X, FileText, Search, Eye, Shield, RefreshCcw, ShieldOff, MessageSquare } from "lucide-react";
import api from "../../lib/api";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { adminNav } from "../../components/shared/navConfig";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";

const STATUS_MAP = {
  not_started: "Pending",
  pending:     "Pending",
  approved:    "Approved",
  rejected:    "Rejected",
};

export default function AdminKycReview() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // { url, filename }
  const [noteFor, setNoteFor] = useState(null); // { user, decision }
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/kyc/all");
      setRows(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const review = async (user_id, decision, name, n = null) => {
    try {
      await api.post(`/admin/kyc/${user_id}/review`, { decision, note: n });
      const label = decision === "approve" ? "Approved" : decision === "reject" ? "Rejected" : "Resubmission requested";
      toast.success(`${label} · ${name}`);
      setNoteFor(null); setNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const act = (user, decision) => {
    if (decision === "approve") return review(user.user_id, "approve", user.name);
    setNoteFor({ user, decision });
  };

  const addNote = async (user_id, name, text) => {
    if (!text?.trim()) return;
    try {
      await api.post(`/admin/users/${user_id}/note`, { note: text });
      toast.success(`Note added · ${name}`);
      setNoteFor(null); setNote("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const suspend = async (user_id, name, suspended) => {
    try {
      await api.post(`/admin/users/${user_id}/suspend`, { suspended });
      toast.success(`${suspended ? "Suspended" : "Reinstated"} · ${name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const view = async (file_id, filename) => {
    try {
      const resp = await api.get(`/files/${file_id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      setPreview({ url, filename });
    } catch { toast.error("Could not open file"); }
  };

  const filtered = rows.filter(r => {
    if (!q) return true;
    const qq = q.toLowerCase();
    return (r.user?.name || "").toLowerCase().includes(qq) || (r.user?.email || "").toLowerCase().includes(qq);
  });

  return (
    <DashboardShell nav={adminNav} title="KYC verification" subtitle="Review student identity documents">
      <div className="card-surface p-5">
        <div className="flex items-center gap-3 mb-5">
          <div className="flex items-center gap-2 px-3 py-2 rounded-full border flex-1 max-w-md" style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
            <Search size={13} className="text-zinc-500"/>
            <input data-testid="kyc-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search students…" className="bg-transparent outline-none text-sm text-white placeholder:text-zinc-600 flex-1"/>
          </div>
          <div className="text-xs text-zinc-500">{filtered.length} record{filtered.length !== 1 && "s"}</div>
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-zinc-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-zinc-500">
            <Shield size={22} className="mx-auto mb-3 text-zinc-600"/>No students to review yet.
          </div>
        ) : (
          <table className="table-stack w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 text-left border-b" style={{ borderColor: "var(--border)" }}>
                <th className="pb-3 font-normal">Student</th>
                <th className="pb-3 font-normal">Document</th>
                <th className="pb-3 font-normal">City</th>
                <th className="pb-3 font-normal">Status</th>
                <th className="pb-3 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(({ user, profile }) => {
                const kycFile = profile?.kyc_file_id;
                const status = STATUS_MAP[user.kyc_status] || "Pending";
                return (
                  <tr key={user.user_id} data-testid={`kyc-row-${user.user_id}`} className="border-b last:border-b-0" style={{ borderColor: "var(--border)" }}>
                    <td className="py-4">
                      <div className="text-white">{user.name}</div>
                      <div className="text-[11px] text-zinc-500">{user.email}</div>
                    </td>
                    <td data-label="Document" className="text-xs">
                      {kycFile ? (
                        <button data-testid={`kyc-view-${user.user_id}`} onClick={() => view(kycFile, "Aadhaar")}
                                className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 transition-colors duration-200">
                          <FileText size={12}/> View document
                        </button>
                      ) : <span className="text-zinc-500">No document</span>}
                    </td>
                    <td data-label="City" className="text-zinc-400 text-xs"><span>{profile?.city || "—"}</span></td>
                    <td data-label="Status"><StatusBadge status={status}/></td>
                    <td className="stack-full">
                      <div className="flex items-center gap-1.5 justify-end">
                        <button data-testid={`kyc-approve-${user.user_id}`} disabled={!kycFile || user.kyc_status === "approved"}
                                onClick={() => act(user, "approve")}
                                className="w-8 h-8 rounded-md border flex items-center justify-center text-emerald-400 hover:bg-emerald-500/10 transition-colors duration-200 disabled:opacity-30"
                                style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }} title="Approve">
                          <Check size={13}/>
                        </button>
                        <button data-testid={`kyc-resubmit-${user.user_id}`} disabled={!kycFile}
                                onClick={() => act(user, "request_resubmit")}
                                className="w-8 h-8 rounded-md border flex items-center justify-center text-amber-400 hover:bg-amber-500/10 transition-colors duration-200 disabled:opacity-30"
                                style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }} title="Request resubmission">
                          <RefreshCcw size={13}/>
                        </button>
                        <button data-testid={`kyc-reject-${user.user_id}`} disabled={!kycFile || user.kyc_status === "rejected"}
                                onClick={() => act(user, "reject")}
                                className="w-8 h-8 rounded-md border flex items-center justify-center text-rose-400 hover:bg-rose-500/10 transition-colors duration-200 disabled:opacity-30"
                                style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }} title="Reject">
                          <X size={13}/>
                        </button>
                        <button data-testid={`kyc-note-${user.user_id}`}
                                onClick={() => setNoteFor({ user, decision: "note" })}
                                className="w-8 h-8 rounded-md border flex items-center justify-center text-zinc-300 hover:bg-white/5 transition-colors duration-200"
                                style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }} title="Add internal note">
                          <MessageSquare size={13}/>
                        </button>
                        <button data-testid={`kyc-suspend-${user.user_id}`}
                                onClick={() => suspend(user.user_id, user.name, !user.suspended)}
                                className={`w-8 h-8 rounded-md border flex items-center justify-center transition-colors duration-200 ${user.suspended ? "text-emerald-400 hover:bg-emerald-500/10" : "text-rose-400 hover:bg-rose-500/10"}`}
                                style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }} title={user.suspended ? "Reinstate" : "Suspend"}>
                          <ShieldOff size={13}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {preview && (
        <div onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}
             className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6">
          <div className="max-w-3xl w-full card-surface p-3" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-2 py-2">
              <div className="text-sm text-white">{preview.filename}</div>
              <button onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }} className="text-zinc-400 hover:text-white"><X size={16}/></button>
            </div>
            <iframe title="doc" src={preview.url} className="w-full h-[70vh] rounded-md border" style={{ borderColor: "var(--border)", background: "var(--surface)" }}/>
          </div>
        </div>
      )}

      <Dialog open={!!noteFor} onOpenChange={() => setNoteFor(null)}>
        <DialogContent className="max-w-md" style={{ background: "var(--surface)", borderColor: "var(--border-strong)", color: "#F8F9FA" }}>
          {noteFor && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">
                  {noteFor.decision === "reject" ? "Reject KYC" :
                   noteFor.decision === "request_resubmit" ? "Request resubmission" :
                   "Internal note"}
                </DialogTitle>
                <DialogDescription className="text-zinc-500">
                  {noteFor.decision === "note" ? "Visible only to admins." : "The note is emailed to the student."}
                </DialogDescription>
              </DialogHeader>
              <textarea data-testid="kyc-note-input" value={note} onChange={e => setNote(e.target.value)} rows={4}
                        placeholder={noteFor.decision === "note" ? "Add context for other admins…" : "Explain what's wrong or needs changing…"}
                        className="w-full px-3 py-2 rounded-md border bg-[#111114] text-sm text-white placeholder:text-zinc-600 outline-none resize-none focus:border-blue-500/50"
                        style={{ borderColor: "var(--border-strong)" }}/>
              <DialogFooter>
                <button onClick={() => { setNoteFor(null); setNote(""); }} className="btn-ghost px-4 py-2 rounded-md text-sm">Cancel</button>
                <button data-testid="kyc-note-confirm" onClick={() => {
                  if (noteFor.decision === "note") addNote(noteFor.user.user_id, noteFor.user.name, note);
                  else review(noteFor.user.user_id, noteFor.decision, noteFor.user.name, note || null);
                }} className="btn-primary px-4 py-2 rounded-md text-sm">Send</button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
